import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Reusable searchable select built on shadcn Command + Popover.
 *
 * Props:
 *  - value: current selected value (string | null)
 *  - onChange: (newValue) => void
 *  - options: array of { value, label }
 *  - placeholder: trigger placeholder when no value
 *  - emptyMessage: shown when search has no matches
 *  - disabled: boolean
 *  - testId: data-testid prefix
 *  - allowClear: show a "Clear" option when value is set
 *  - clearLabel: text for the clear option
 */
export const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyMessage = 'No results.',
  disabled = false,
  testId = 'searchable-select',
  allowClear = false,
  clearLabel = 'Clear',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className={`w-full justify-between bg-gray-950 border-gray-700 text-gray-100 hover:bg-gray-900 hover:text-gray-100 ${
            !selected ? 'text-gray-500' : ''
          } ${className}`}
        >
          {selected ? selected.label : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-gray-900 border border-gray-800 text-gray-200 z-50"
      >
        <Command className="bg-transparent" shouldFilter={true}>
          <CommandInput
            placeholder="Search…"
            className="text-gray-100 placeholder:text-gray-500"
            data-testid={`${testId}-input`}
          />
          <CommandList className="max-h-72">
            <CommandEmpty className="text-gray-500 py-4 text-center text-sm">
              {emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="__all__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className={`cursor-pointer ${
                    value === null || value === undefined
                      ? 'text-red-400 aria-selected:bg-red-600/20'
                      : 'text-gray-300 aria-selected:bg-red-600/10 aria-selected:text-red-300'
                  }`}
                  data-testid={`${testId}-all`}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      !value ? 'opacity-100 text-red-500' : 'opacity-0'
                    }`}
                  />
                  {clearLabel}
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className="text-gray-200 aria-selected:bg-red-600 aria-selected:text-white cursor-pointer"
                  data-testid={`${testId}-option-${opt.value}`}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value === opt.value ? 'opacity-100 text-red-500' : 'opacity-0'
                    }`}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
